(ns practitest.frontend.integration-settings.entity-mapping.events
  (:require [re-frame.core :as re-frame]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.components.notifications :as notifications]
            [ajax.core :as ajax]))

(def ^:private default-requirements-types #{"User Story"})
(def ^:private default-issues-types #{"Bug" "Task"})

(defn- apply-defaults
  [work-item-types]
  (let [all-ids     (map :id work-item-types)
        req-types   (vec (filter #(contains? default-requirements-types %) all-ids))
        issue-types (vec (filter #(contains? default-issues-types %) all-ids))]
    {:requirements {:ex_type req-types}
     :issues       {:ex_type issue-types}}))

(defn- build-sp-map [types]
  (into {} (map (fn [t] [(:id t) (boolean (:supports_parent t))]) types)))

(defn- new-project-mapping [bugtracker work-item-types-map pid-key]
  (if (= bugtracker "Youtrack")
    {:issues {:ex_type ["Bug"]}}
    (let [pid-str (name pid-key)
          types   (get work-item-types-map pid-str)]
      (if types
        (assoc (apply-defaults types) :type_supports_parent (build-sp-map types))
        {:requirements       {:ex_type []}
         :issues             {:ex_type []}
         :_pending_defaults? true}))))

(defn- dedupe-project-mapping [mapping]
  (let [issue-types (set (get-in mapping [:issues :ex_type] []))
        req-types   (get-in mapping [:requirements :ex_type] [])]
    (assoc-in mapping [:requirements :ex_type] (vec (remove issue-types req-types)))))

(defn- dedupe-project-mappings [project-mappings]
  (into {} (map (fn [[pid mapping]] [pid (dedupe-project-mapping mapping)]) project-mappings)))

;; Serialized lazy-loading of types. Azure fetches per project-id; ClickUp per space-id;
;; Youtrack has exactly one legal type ("Bug"), so it never needs a live fetch.
(defn- chain-next-fetch [db]
  (let [bugtracker (get-in db [:entity-mapping :bugtracker])
        cached     (get-in db [:entity-mapping :project-work-item-types] {})
        loading    (get-in db [:entity-mapping :project-types-loading] #{})
        failed     (get-in db [:entity-mapping :project-types-failed] #{})]
    (cond
      (= bugtracker "ClickUp")
      (let [space-ids (->> (concat
                            (map :space_id (get-in db [:entity-mapping :configured-lists] []))
                            (get-in db [:entity-mapping :selected-space-ids] #{}))
                           (remove nil?)
                           distinct)]
        (when-let [sid (->> space-ids
                            (remove #(contains? cached %))
                            (remove #(contains? loading %))
                            (remove #(contains? failed %))
                            first)]
          [[:dispatch [::fetch-space-task-types sid]]]))

      (= bugtracker "Youtrack")
      nil

      :else
      (let [selected (get-in db [:entity-mapping :selected-project-ids] #{})]
        (when-let [pid (->> selected
                            (map name)
                            (remove #(contains? cached %))
                            (remove #(contains? loading %))
                            (remove #(contains? failed %))
                            first)]
          [[:dispatch [::fetch-project-work-item-types pid]]])))))

(re-frame/reg-event-db
 ::initialize-db
 (fn [db [_ {:keys [project-id integration-id integration-name bugtracker]}]]
   (assoc db :entity-mapping
          {:project-id              project-id
           :integration-id          integration-id
           :integration-name        integration-name
           :bugtracker              bugtracker
           :loading?                true
           :projects-loading?       true
           :selected-project-ids    #{}
           :available-projects      []
           :configured-lists        []
           :configured-projects     []
           ;; ClickUp space/list selection
           :available-spaces        []
           :spaces-loading?         false
           :selected-space-ids      #{}
           :available-lists-by-space {}
           :lists-loading           #{}
           :project-work-item-types {}
           :project-types-loading   #{}
           :project-types-failed    #{}
           :data                    nil
           :errors                  []})))

(re-frame/reg-event-fx
 ::fetch-entity-mapping
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)]
     {:http-xhrio {:method          :get
                   :uri             (broutes/get-url "two_way_integration_settings#entity_mapping"
                                                     {:project_id project-id
                                                      :id         integration-id})
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::fetch-entity-mapping-success]
                   :on-failure      [::fetch-entity-mapping-failure]}})))

(re-frame/reg-event-fx
 ::fetch-entity-mapping-success
 (fn [{:keys [db]} [_ response]]
   (let [bugtracker       (get-in db [:entity-mapping :bugtracker])
         clickup?         (= bugtracker "ClickUp")
         project-mappings (dedupe-project-mappings (or (:project_mappings response) {}))
         response         (assoc response :project_mappings project-mappings)
         mapped-pids      (keys project-mappings)
         configured-lists (vec (:configured_lists response []))
         configured-projects (vec (:configured_projects response []))
         pre-space-ids    (when clickup?
                            (->> configured-lists (keep :space_id) (filter seq) set))
         seeded-lists     (when (seq pre-space-ids)
                            (->> configured-lists
                                 (filter #(seq (:space_id %)))
                                 (group-by :space_id)
                                 (reduce-kv (fn [m sid lists]
                                              (assoc m (keyword sid)
                                                     (mapv (fn [l] {:id (:id l) :label (:name l) :space_id sid}) lists)))
                                            {})))
         db'              (cond-> (update db :entity-mapping merge
                                          {:loading?                   false
                                           :data                       response
                                           :saved-data                 response
                                           :selected-project-ids       (set mapped-pids)
                                           :saved-selected-project-ids (set mapped-pids)
                                           :configured-lists           configured-lists
                                           :configured-projects        configured-projects
                                           :errors                     []})
                            pre-space-ids (assoc-in [:entity-mapping :selected-space-ids] pre-space-ids)
                            pre-space-ids (assoc-in [:entity-mapping :saved-selected-space-ids] pre-space-ids)
                            seeded-lists  (assoc-in [:entity-mapping :available-lists-by-space] seeded-lists)
                            clickup?      (assoc-in [:entity-mapping :spaces-loading?] true))]
     {:db db'
      :fx (cond-> (or (chain-next-fetch db') [])
            clickup? (conj [:dispatch [::fetch-available-spaces]])
            (seq pre-space-ids) (conj [:dispatch [::fetch-lists-for-spaces (vec pre-space-ids)]]))})))

(re-frame/reg-event-fx
 ::fetch-entity-mapping-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :error]
                               (get-translation "bugtracker.integration-settings.error.fetch-failed"
                                                "Failed to load entity mapping"))]
     {:db       (update db :entity-mapping merge {:loading? false})
      :dispatch [::notifications/error2 error-message]})))

;; Azure + Youtrack — flat project list, no space/list two-tier
(re-frame/reg-event-fx
 ::fetch-available-projects
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id bugtracker]} (:entity-mapping db)
         route (if (= bugtracker "Youtrack")
                 "youtrack_integration_settings#available_youtrack_projects"
                 "azure_integration_settings#available_azure_projects")]
     {:db         (assoc-in db [:entity-mapping :projects-loading?] true)
      :http-xhrio {:method          :get
                   :uri             (broutes/get-url route
                                                     {:project_id project-id
                                                      :id         integration-id})
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::fetch-available-projects-success]
                   :on-failure      [::fetch-available-projects-failure]}})))

(re-frame/reg-event-db
 ::fetch-available-projects-success
 (fn [db [_ response]]
   (update db :entity-mapping merge
           {:projects-loading?  false
            :available-projects (vec (:available_projects response []))})))

(re-frame/reg-event-fx
 ::fetch-available-projects-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :error]
                               (format-translation (get-translation "bugtracker.integration-settings.error.projects-fetch-failed"
                                                                    "Failed to load %{bugtracker} projects")
                                                   :bugtracker "Azure DevOps"))]
     {:db       (assoc-in db [:entity-mapping :projects-loading?] false)
      :dispatch [::notifications/error2 error-message]})))

;; Azure only
(re-frame/reg-event-fx
 ::fetch-project-work-item-types
 (fn [{:keys [db]} [_ azure-project-id]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)
         loading-set     (get-in db [:entity-mapping :project-types-loading] #{})
         types-cache     (get-in db [:entity-mapping :project-work-item-types] {})
         already-active? (or (contains? loading-set azure-project-id)
                             (contains? types-cache azure-project-id))]
     (if already-active?
       {}
       {:db         (update-in db [:entity-mapping :project-types-loading] (fnil conj #{}) azure-project-id)
        :http-xhrio {:method          :get
                     :uri             (broutes/get-url "two_way_integration_settings#entity_mapping_work_item_types"
                                                       {:project_id project-id
                                                        :id         integration-id})
                     :params          {:azure_project_id azure-project-id}
                     :response-format (ajax/json-response-format {:keywords? true})
                     :on-success      [::fetch-project-work-item-types-success azure-project-id]
                     :on-failure      [::fetch-project-work-item-types-failure azure-project-id]}}))))

(re-frame/reg-event-fx
 ::fetch-project-work-item-types-success
 (fn [{:keys [db]} [_ azure-project-id response]]
   (let [pid-key         (keyword azure-project-id)
         work-item-types (vec response)
         has-mapping?    (some? (get-in db [:entity-mapping :data :project_mappings pid-key]))
         db'             (cond-> db
                           true         (update-in [:entity-mapping :project-work-item-types] assoc azure-project-id work-item-types)
                           true         (update-in [:entity-mapping :project-types-loading] (fnil disj #{}) azure-project-id)
                           (and has-mapping?
                                (get-in db [:entity-mapping :data :project_mappings pid-key :_pending_defaults?]))
                           (update-in [:entity-mapping :data :project_mappings pid-key]
                                      (fn [m]
                                        (-> (apply-defaults work-item-types)
                                            (assoc :project_name (:project_name m))
                                            (assoc :type_supports_parent (build-sp-map work-item-types))
                                            (dissoc :_pending_defaults?)))))]
     {:db db'
      :fx (or (chain-next-fetch db') [])})))

(re-frame/reg-event-fx
 ::fetch-project-work-item-types-failure
 (fn [{:keys [db]} [_ azure-project-id _response]]
   (let [db' (-> db
                 (update-in [:entity-mapping :project-types-loading] (fnil disj #{}) azure-project-id)
                 (update-in [:entity-mapping :project-types-failed] (fnil conj #{}) azure-project-id))]
     {:db db'
      :fx (into [[:dispatch [::notifications/error2
                             (format-translation (get-translation "bugtracker.integration-settings.error.types-fetch-failed"
                                                                  "Failed to load %{bugtracker} types for one of the projects")
                                                :bugtracker "Azure DevOps")]]]
                (chain-next-fetch db'))})))

;; ClickUp only
(re-frame/reg-event-fx
 ::fetch-space-task-types
 (fn [{:keys [db]} [_ space-id]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)
         loading-set     (get-in db [:entity-mapping :project-types-loading] #{})
         types-cache     (get-in db [:entity-mapping :project-work-item-types] {})
         already-active? (or (contains? loading-set space-id)
                             (contains? types-cache space-id))]
     (if already-active?
       {}
       {:db         (update-in db [:entity-mapping :project-types-loading] (fnil conj #{}) space-id)
        :http-xhrio {:method          :get
                     :uri             (broutes/get-url "clickup_integration_settings#entity_mapping_space_task_types"
                                                       {:project_id project-id
                                                        :id         integration-id})
                     :params          {:space_id space-id}
                     :response-format (ajax/json-response-format {:keywords? true})
                     :on-success      [::fetch-space-task-types-success space-id]
                     :on-failure      [::fetch-space-task-types-failure space-id]}}))))

(re-frame/reg-event-fx
 ::fetch-space-task-types-success
 (fn [{:keys [db]} [_ space-id response]]
   (let [task-types (vec (if (sequential? response) response (:data response [])))
         db'        (-> db
                        (update-in [:entity-mapping :project-work-item-types] assoc space-id task-types)
                        (update-in [:entity-mapping :project-types-loading] (fnil disj #{}) space-id))]
     {:db db'
      :fx (or (chain-next-fetch db') [])})))

(re-frame/reg-event-fx
 ::fetch-space-task-types-failure
 (fn [{:keys [db]} [_ space-id _response]]
   (let [db' (-> db
                 (update-in [:entity-mapping :project-types-loading] (fnil disj #{}) space-id)
                 (update-in [:entity-mapping :project-types-failed] (fnil conj #{}) space-id))]
     {:db db'
      :fx (into [[:dispatch [::notifications/error2
                             (format-translation (get-translation "bugtracker.integration-settings.error.types-fetch-failed"
                                                                  "Failed to load %{bugtracker} types for one of the projects")
                                                :bugtracker "ClickUp")]]]
                (chain-next-fetch db'))})))

;; ClickUp only — space/list selection

(re-frame/reg-event-fx
 ::fetch-available-spaces
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)]
     {:db         (assoc-in db [:entity-mapping :spaces-loading?] true)
      :http-xhrio {:method          :get
                   :uri             (broutes/get-url "clickup_integration_settings#available_clickup_spaces"
                                                     {:project_id project-id :id integration-id})
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::fetch-available-spaces-success]
                   :on-failure      [::fetch-available-spaces-failure]}})))

(re-frame/reg-event-fx
 ::fetch-available-spaces-success
 (fn [{:keys [db]} [_ response]]
   (let [spaces           (vec (:available_spaces response []))
         selected-ids     (get-in db [:entity-mapping :selected-space-ids] #{})
         space-ids-to-fetch (->> selected-ids
                                 (remove #(contains? (get-in db [:entity-mapping :available-lists-by-space] {}) %))
                                 seq)
         db'              (update db :entity-mapping merge
                                  {:available-spaces spaces
                                   :spaces-loading?  false})]
     {:db db'
      :fx (when space-ids-to-fetch
            [[:dispatch [::fetch-lists-for-spaces (vec space-ids-to-fetch)]]])})))

(re-frame/reg-event-db
 ::fetch-available-spaces-failure
 (fn [db [_ _response]]
   (assoc-in db [:entity-mapping :spaces-loading?] false)))

(re-frame/reg-event-fx
 ::fetch-lists-for-spaces
 (fn [{:keys [db]} [_ space-ids]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)
         [first-space & remaining]            space-ids]
     (when first-space
       {:db (update-in db [:entity-mapping :lists-loading] (fnil conj #{}) first-space)
        :http-xhrio {:method          :get
                     :uri             (broutes/get-url "clickup_integration_settings#available_clickup_lists"
                                                       {:project_id project-id :id integration-id})
                     :params          {:space_ids [first-space]}
                     :response-format (ajax/json-response-format {:keywords? true})
                     :on-success      [::fetch-lists-for-spaces-success [first-space] (vec remaining)]
                     :on-failure      [::fetch-lists-for-spaces-failure [first-space] (vec remaining)]}}))))

(re-frame/reg-event-fx
 ::fetch-lists-for-spaces-success
 (fn [{:keys [db]} [_ space-ids remaining response]]
   (let [lists-map      (:lists_by_space response {})
         new-loading    (reduce disj (get-in db [:entity-mapping :lists-loading] #{}) space-ids)
         updated-cache  (reduce (fn [cache sid]
                                  (let [api-lists (let [v (get lists-map (keyword sid))]
                                                    (when (sequential? v) v))]
                                    (if (seq api-lists)
                                      (assoc cache (keyword sid)
                                             (vec (map (fn [l] {:id (:id l) :label (:name l) :space_id sid})
                                                       api-lists)))
                                      cache)))
                                (get-in db [:entity-mapping :available-lists-by-space] {})
                                space-ids)
         db'            (-> db
                            (assoc-in [:entity-mapping :lists-loading] new-loading)
                            (assoc-in [:entity-mapping :available-lists-by-space] updated-cache))]
     {:db db'
      :fx (or (when (seq remaining)
                [[:dispatch [::fetch-lists-for-spaces remaining]]])
              [])})))

(re-frame/reg-event-fx
 ::fetch-lists-for-spaces-failure
 (fn [{:keys [db]} [_ space-ids remaining _response]]
   (let [new-loading (reduce disj (get-in db [:entity-mapping :lists-loading] #{}) space-ids)]
     {:db (assoc-in db [:entity-mapping :lists-loading] new-loading)
      :fx (cond-> [[:dispatch [::notifications/error2
                               (format-translation (get-translation "bugtracker.integration-settings.error.types-fetch-failed"
                                                                    "Failed to load %{bugtracker} types for one of the projects")
                                                   :bugtracker "ClickUp")]]]
             (seq remaining) (conj [:dispatch [::fetch-lists-for-spaces remaining]]))})))

(re-frame/reg-event-fx
 ::update-selected-spaces
 (fn [{:keys [db]} [_ selected-space-ids]]
   (let [available-space-ids (set (map :id (get-in db [:entity-mapping :available-spaces] [])))
         prev-space-ids      (get-in db [:entity-mapping :selected-space-ids] #{})
         locked-space-ids    (remove available-space-ids prev-space-ids)
         new-checked-ids     (set selected-space-ids)
         added-spaces        (remove prev-space-ids new-checked-ids)
         removed-spaces      (remove new-checked-ids (filter available-space-ids prev-space-ids))
         final-space-ids     (into (set locked-space-ids) new-checked-ids)
         lists-by-space      (get-in db [:entity-mapping :available-lists-by-space] {})
         removed-list-ids    (set (mapcat (fn [sid]
                                            (map (comp keyword :id) (get lists-by-space (keyword sid) [])))
                                          removed-spaces))
         current-selected    (get-in db [:entity-mapping :selected-project-ids] #{})
         new-selected        (remove #(contains? removed-list-ids %) current-selected)
         new-mappings        (reduce dissoc
                                     (get-in db [:entity-mapping :data :project_mappings] {})
                                     removed-list-ids)
         removed-space-keys  (set (map keyword removed-spaces))
         new-space-mappings  (reduce dissoc
                                     (get-in db [:entity-mapping :data :space_mappings] {})
                                     removed-space-keys)
         db'                 (-> db
                                 (assoc-in [:entity-mapping :selected-space-ids] final-space-ids)
                                 (assoc-in [:entity-mapping :selected-project-ids] (set new-selected))
                                 (assoc-in [:entity-mapping :data :project_mappings] new-mappings)
                                 (assoc-in [:entity-mapping :data :space_mappings] new-space-mappings))]
     {:db db'
      :fx (when (seq added-spaces)
            [[:dispatch [::fetch-lists-for-spaces (vec added-spaces)]]])})))

(re-frame/reg-event-fx
 ::update-selected-lists
 (fn [{:keys [db]} [_ selected-list-ids]]
   (let [available-space-ids (set (map (comp keyword :id) (get-in db [:entity-mapping :available-spaces] [])))
         lists-by-space   (get-in db [:entity-mapping :available-lists-by-space] {})
         accessible-lists-by-space (select-keys lists-by-space available-space-ids)
         all-lists        (mapcat val accessible-lists-by-space)
         available-ids    (set (map (comp keyword :id) all-lists))
         prev-selected    (get-in db [:entity-mapping :selected-project-ids] #{})
         locked-list-ids  (remove available-ids prev-selected)
         all-selected-ids (distinct (concat selected-list-ids locked-list-ids))
         current-mappings (get-in db [:entity-mapping :data :project_mappings] {})
         new-mappings     (reduce
                           (fn [acc lid]
                             (assoc acc (keyword lid)
                                    (get current-mappings (keyword lid)
                                         {:requirements {:ex_type []}
                                          :issues       {:ex_type ["Task"]}})))
                           {}
                           all-selected-ids)
         db'              (-> db
                              (assoc-in [:entity-mapping :selected-project-ids] (set all-selected-ids))
                              (assoc-in [:entity-mapping :data :project_mappings] new-mappings))]
     {:db db'
      :fx (or (chain-next-fetch db') [])})))

(re-frame/reg-event-db
 ::toggle-requirements-sync
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :requirements_sync_enabled] enabled?)))

(re-frame/reg-event-db
 ::toggle-issues-sync
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :issues_sync_enabled] enabled?)))

(re-frame/reg-event-db
 ::toggle-sync-all-lists
 (fn [db [_ enabled?]]
   (cond-> (assoc-in db [:entity-mapping :data :sync_all_lists] enabled?)
     enabled?
     (assoc-in [:entity-mapping :data :space_mappings]
               (let [selected-space-ids     (get-in db [:entity-mapping :selected-space-ids] #{})
                     current-space-mappings (get-in db [:entity-mapping :data :space_mappings] {})]
                 (reduce (fn [acc sid]
                          (let [sid-key (keyword sid)]
                            (if (contains? acc sid-key)
                              acc
                              (assoc acc sid-key {:requirements {:ex_type []}
                                                  :issues       {:ex_type ["Task"]}}))))
                        current-space-mappings
                        selected-space-ids))))))

(defn- update-entity-mapping-field
  [db mapping-key id-key entity-type field value]
  (let [db (assoc-in db [:entity-mapping :data mapping-key id-key entity-type field] value)]
    (if (= field :ex_type)
      (let [other-entity-type (if (= entity-type :requirements) :issues :requirements)
            other-path        [:entity-mapping :data mapping-key id-key other-entity-type :ex_type]]
        (update-in db other-path (fn [other-types] (vec (remove (set value) (or other-types []))))))
      db)))

(re-frame/reg-event-db
 ::update-space-mapping
 (fn [db [_ space-id entity-type field value]]
   (update-entity-mapping-field db :space_mappings (keyword space-id) entity-type field value)))

(re-frame/reg-event-db
 ::toggle-space-entity-status
 (fn [db [_ space-id entity-type]]
   (let [sid-key          (keyword space-id)
         current-mappings (get-in db [:entity-mapping :data :space_mappings] {})
         entity-path      [sid-key entity-type]
         current-entity   (get-in current-mappings entity-path)
         is-enabling?     (:disabled current-entity false)
         updated-entity   (if is-enabling? {:ex_type []} {:ex_type [] :disabled true})]
     (assoc-in db [:entity-mapping :data :space_mappings]
               (assoc-in current-mappings entity-path updated-entity)))))

;; Azure only
(re-frame/reg-event-fx
 ::update-selected-projects
 (fn [{:keys [db]} [_ selected-project-ids]]
   (let [bugtracker          (get-in db [:entity-mapping :bugtracker])
         current-mappings    (get-in db [:entity-mapping :data :project_mappings] {})
         available-projects  (get-in db [:entity-mapping :available-projects] [])
         work-item-types-map (get-in db [:entity-mapping :project-work-item-types] {})
         projects-by-id      (into {} (map (fn [p] [(keyword (:id p)) (:name p)]) available-projects))
         new-mappings
         (reduce
          (fn [acc pid-key]
            (let [mapping (if (contains? current-mappings pid-key)
                            (get current-mappings pid-key)
                            (new-project-mapping bugtracker work-item-types-map pid-key))
                  p-name  (get projects-by-id pid-key)]
              (cond-> (assoc acc pid-key mapping)
                p-name (assoc-in [pid-key :project_name] p-name))))
          {}
          selected-project-ids)
         db' (-> db
                 (assoc-in [:entity-mapping :selected-project-ids] selected-project-ids)
                 (assoc-in [:entity-mapping :data :project_mappings] new-mappings))]
     {:db db'
      :fx (or (when (empty? (get-in db' [:entity-mapping :project-types-loading] #{}))
                (chain-next-fetch db'))
              [])})))

(re-frame/reg-event-db
 ::update-project-mapping
 (fn [db [_ project-id entity-type field value]]
   (update-entity-mapping-field db :project_mappings (keyword project-id) entity-type field value)))

(re-frame/reg-event-db
 ::toggle-project-entity-status
 (fn [db [_ project-id entity-type]]
   (let [bugtracker      (get-in db [:entity-mapping :bugtracker])
         pid-key         (keyword project-id)
         current-mappings (get-in db [:entity-mapping :data :project_mappings] {})
         entity-path     [pid-key entity-type]
         current-entity  (get-in current-mappings entity-path)
         is-enabling?    (:disabled current-entity false)
         work-item-types (get-in db [:entity-mapping :project-work-item-types project-id])
         youtrack-bug-taken-by-requirements? (some #{"Bug"} (get-in current-mappings [pid-key :requirements :ex_type]))
         updated-entity  (if is-enabling?
                           (cond
                             (and (= bugtracker "AzureDevops") work-item-types)
                             {:ex_type (vec (filter #(contains? (if (= entity-type :requirements)
                                                                  default-requirements-types
                                                                  default-issues-types) %)
                                                    (map :id work-item-types)))}

                             (and (= bugtracker "Youtrack") (= entity-type :issues)
                                  (not youtrack-bug-taken-by-requirements?))
                             {:ex_type ["Bug"]}

                             :else
                             {:ex_type []})
                           {:ex_type [] :disabled true})]
     (assoc-in db [:entity-mapping :data :project_mappings]
               (assoc-in current-mappings entity-path updated-entity)))))

(re-frame/reg-event-db
 ::toggle-allow-issue-creation
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :allow_issue_creation] enabled?)))

(re-frame/reg-event-db
 ::toggle-use-pt-id
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :use_pt_id] enabled?)))

(re-frame/reg-event-db
 ::toggle-skip-mapping-manual-import
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :skip_mapping_in_manual_import] enabled?)))

;; Youtrack only
(re-frame/reg-event-db
 ::update-default-project
 (fn [db [_ project-id]]
   (assoc-in db [:entity-mapping :data :default_project] project-id)))

(re-frame/reg-event-fx
 ::save-settings
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id data bugtracker available-projects]} (:entity-mapping db)
         work-item-types-map (get-in db [:entity-mapping :project-work-item-types] {})
         payload (cond
                   (= bugtracker "AzureDevops")
                   (let [projects-by-id    (into {} (map (fn [p] [(keyword (:id p)) (:name p)]) available-projects))
                         enriched-mappings (reduce-kv
                                            (fn [m pid _mapping]
                                              (let [p-name     (get projects-by-id pid)
                                                    types-list (get work-item-types-map (name pid) [])
                                                    sp-map     (when (seq types-list) (build-sp-map types-list))]
                                                (cond-> m
                                                  p-name (assoc-in [pid :project_name] p-name)
                                                  sp-map (assoc-in [pid :type_supports_parent] sp-map))))
                                            (:project_mappings data {})
                                            (:project_mappings data {}))]
                     (assoc data :project_mappings enriched-mappings))
                   (= bugtracker "ClickUp")
                   (let [available-spaces   (get-in db [:entity-mapping :available-spaces] [])
                         space-names        (into {} (map (fn [s] [(:id s) (:name s)]) available-spaces))
                         lists-by-space     (get-in db [:entity-mapping :available-lists-by-space] {})
                         configured-lists   (get-in db [:entity-mapping :configured-lists] [])
                         configured-by-id   (into {} (map (fn [l] [(:id l) l]) configured-lists))
                         all-lists-index    (into {}
                                                  (concat
                                                   (map (fn [l] [(:id l) l]) configured-lists)
                                                   (mapcat (fn [[_sid lists]]
                                                             (map (fn [l] [(:id l) l]) lists))
                                                           lists-by-space)))
                         enriched-mappings  (reduce-kv
                                             (fn [m pid mapping]
                                               (let [lid-str (name pid)
                                                     list    (get all-lists-index lid-str)
                                                     l-name  (or (:label list) (:name list)
                                                                  (get-in mapping [:list_name])
                                                                  (get-in mapping ["list_name"]))
                                                     s-id    (or (:space_id list)
                                                                  (get-in mapping [:space_id])
                                                                  (get-in mapping ["space_id"]))
                                                     s-name  (or (get space-names s-id)
                                                                  (:space_name (get configured-by-id lid-str))
                                                                  (get-in mapping [:space_name])
                                                                  (get-in mapping ["space_name"]))]
                                                 (cond-> m
                                                   l-name (assoc-in [pid :list_name] l-name)
                                                   s-id   (assoc-in [pid :space_id] s-id)
                                                   s-name (assoc-in [pid :space_name] s-name))))
                                             (:project_mappings data {})
                                             (:project_mappings data {}))
                         enriched-space-mappings
                         (reduce-kv
                          (fn [m sid _mapping]
                            (if-let [s-name (get space-names (name sid))]
                              (assoc-in m [sid :space_name] s-name)
                              m))
                          (:space_mappings data {})
                          (:space_mappings data {}))]
                     (assoc data
                            :project_mappings enriched-mappings
                            :space_mappings enriched-space-mappings))
                   :else data)]
     {:db (assoc-in db [:entity-mapping :saving?] true)
      :http-xhrio {:method          :put
                   :uri             (broutes/get-url "two_way_integration_settings#update_entity_mapping"
                                                     {:project_id project-id
                                                      :id         integration-id})
                   :params          payload
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::save-settings-success]
                   :on-failure      [::save-settings-failure]}})))

(re-frame/reg-event-fx
 ::save-settings-success
 (fn [{:keys [db]} [_]]
   (let [current-data       (get-in db [:entity-mapping :data])
         current-selected   (get-in db [:entity-mapping :selected-project-ids])
         current-space-ids  (get-in db [:entity-mapping :selected-space-ids])]
     {:db       (update db :entity-mapping merge
                        {:saving?                    false
                         :saved-data                 current-data
                         :saved-selected-project-ids current-selected
                         :saved-selected-space-ids   current-space-ids
                         :errors                     []})
      :dispatch [::notifications/success2
                 (get-translation "bugtracker.integration-settings.save.success"
                                  "Settings saved successfully")]})))

(re-frame/reg-event-fx
 ::save-settings-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :error]
                               (get-translation "bugtracker.integration-settings.error.save-failed"
                                                "Failed to save entity mapping"))]
     {:db       (assoc-in db [:entity-mapping :saving?] false)
      :dispatch [::notifications/error2 error-message]})))

(re-frame/reg-event-db
 ::cancel-changes
 (fn [db [_]]
   (let [saved-data      (get-in db [:entity-mapping :saved-data])
         saved-selected  (get-in db [:entity-mapping :saved-selected-project-ids])
         saved-space-ids (get-in db [:entity-mapping :saved-selected-space-ids])]
     (-> db
         (assoc-in [:entity-mapping :data] saved-data)
         (assoc-in [:entity-mapping :selected-project-ids] saved-selected)
         (assoc-in [:entity-mapping :selected-space-ids] saved-space-ids)))))
